/**
 * Google `Marker` adapter — wraps `@vis.gl/react-google-maps`'s
 * `AdvancedMarker` + `PinElement` so a TWM consumer can render colored,
 * draggable pins without importing the Google package directly.
 *
 * Hover tooltip uses an `InfoWindow` attached on mouseenter and removed on
 * mouseleave — same UX as the Mapbox adapter's popup-on-hover.
 *
 * AdvancedMarker requires a Map ID on the parent map; without one, Google's
 * loader logs a warning and falls back to legacy markers (which lose the
 * `draggable` setter). The MapView reads the Map ID env and passes it on,
 * so as long as `VITE_GOOGLE_MAPS_MAP_ID` / `NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID`
 * are set, this just works.
 *
 * Phase 4 (#325) additions for the display-map migration:
 *  - `popup` (ReactNode) + `popupOpen` (bool) — controlled rich popup,
 *    rendered into an `InfoWindow` anchored at the marker position.
 *  - `iconElement` (ReactNode) — render custom marker content instead of
 *    the colored PinElement. Used by LiveTracking's truck emoji marker.
 *  - `zIndex` + `scale` — visual emphasis for selected markers.
 */

import { useEffect, useRef, useState } from 'react';
import type * as React from 'react';
import {
  AdvancedMarker,
  InfoWindow,
  useMap as useGoogleMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import type { MarkerProps } from '../../types';

export const GoogleMarker: React.FC<MarkerProps> = ({
  position,
  color = '#f97316',
  draggable = false,
  onDragStart,
  onDrag,
  onDragEnd,
  onClick,
  onMouseEnter,
  onMouseLeave,
  tooltip,
  popup,
  popupOpen,
  iconElement,
  zIndex,
  scale,
}) => {
  const map = useGoogleMap();
  const markerLib = useMapsLibrary('marker');
  const [pinContent, setPinContent] = useState<HTMLElement | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Refs let us read latest handler / position inside Google's listener
  // callbacks without recreating the marker on every prop change.
  const onDragStartRef = useRef(onDragStart);
  const onDragRef = useRef(onDrag);
  const onDragEndRef = useRef(onDragEnd);
  const onClickRef = useRef(onClick);
  const onMouseEnterRef = useRef(onMouseEnter);
  const onMouseLeaveRef = useRef(onMouseLeave);
  useEffect(() => { onDragStartRef.current = onDragStart; }, [onDragStart]);
  useEffect(() => { onDragRef.current = onDrag; }, [onDrag]);
  useEffect(() => { onDragEndRef.current = onDragEnd; }, [onDragEnd]);
  useEffect(() => { onClickRef.current = onClick; }, [onClick]);
  useEffect(() => { onMouseEnterRef.current = onMouseEnter; }, [onMouseEnter]);
  useEffect(() => { onMouseLeaveRef.current = onMouseLeave; }, [onMouseLeave]);

  // Build a PinElement on color change so we get a Mapbox-style colored
  // teardrop instead of the default red marker. Skipped if the marker library
  // hasn't loaded yet or if the consumer is providing their own iconElement.
  useEffect(() => {
    if (!markerLib) return;
    if (iconElement) {
      setPinContent(null);
      return;
    }
    const pin = new markerLib.PinElement({
      background: color,
      borderColor: '#ffffff',
      glyphColor: '#ffffff',
      scale: scale ?? 1,
    });
    setPinContent(pin.element);
  }, [markerLib, color, scale, iconElement]);

  // Hide-while-dragging is the consumer's concern (LocationPicker hides the
  // popover during drag, etc). We just emit the drag stream.
  if (!map) return null;

  return (
    <>
      <AdvancedMarker
        position={{ lat: position.lat, lng: position.lng }}
        draggable={draggable}
        zIndex={zIndex}
        onClick={() => onClickRef.current?.()}
        onMouseEnter={() => {
          if (tooltip) setTooltipOpen(true);
          onMouseEnterRef.current?.();
        }}
        onMouseLeave={() => {
          setTooltipOpen(false);
          onMouseLeaveRef.current?.();
        }}
        onDragStart={() => onDragStartRef.current?.()}
        onDrag={(e) => {
          const ll = e.latLng;
          if (!ll) return;
          onDragRef.current?.({ lat: ll.lat(), lng: ll.lng() });
        }}
        onDragEnd={(e) => {
          const ll = e.latLng;
          if (!ll) return;
          onDragEndRef.current?.({ lat: ll.lat(), lng: ll.lng() });
        }}
      >
        {/* Render order: consumer-supplied icon > PinElement host > nothing. */}
        {iconElement ? (
          <div style={scale && scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: 'bottom center' } : undefined}>
            {iconElement}
          </div>
        ) : pinContent ? (
          <PinHost element={pinContent} />
        ) : null}
      </AdvancedMarker>
      {tooltip && tooltipOpen && (
        <InfoWindow
          position={{ lat: position.lat, lng: position.lng }}
          pixelOffset={[0, -24]}
          onCloseClick={() => setTooltipOpen(false)}
        >
          <div style={{ fontSize: 12 }}>{tooltip}</div>
        </InfoWindow>
      )}
      {popup && popupOpen && (
        <InfoWindow
          position={{ lat: position.lat, lng: position.lng }}
          pixelOffset={[0, -32]}
          onCloseClick={() => {/* parent controls open state */}}
        >
          {popup}
        </InfoWindow>
      )}
    </>
  );
};

/**
 * Wraps a raw DOM `HTMLElement` as a React child by attaching it to a host
 * div on mount. AdvancedMarker accepts React children for its content slot,
 * and the cleanest way to surface a `PinElement` (which is itself an HTML
 * element) is to wrap it through a tiny host.
 */
const PinHost: React.FC<{ element: HTMLElement }> = ({ element }) => {
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    host.appendChild(element);
    return () => {
      if (element.parentNode === host) host.removeChild(element);
    };
  }, [element]);
  return <div ref={hostRef} />;
};
